import React, { PureComponent, Fragment } from 'react';

import { Grid } from 'components/grid';
import Html from 'components/Html';
import { translate } from 'components/translate';
import HttpCodes from 'configs/HttpCodes';
import { formatBytes } from 'helpers/File';
import flowRight from 'lodash/flowRight';
import { inject } from 'mobx-react';
import { DatahubEventsService } from 'modules/app/services';
import { TryMediaUploadSchema } from 'modules/app/services/datahubEvents/media';
import { FileLibraryProvider } from 'modules/configManager/types';
import { PlayerService } from 'modules/player';
import {
  PLAYER_STATUS_CATEGORY,
  PLAYER_STATUS_CATEGORIES
} from 'modules/player/constants';
import { SubtitleSection } from 'modules/subtitle';
import PropTypes from 'prop-types';

import FilePlayerEmbed from './FilePlayerEmbed';
import {
  FilePlayerRemoveModal,
  FilePlayerReplaceModal
} from './FilePlayerModals';
import FilePlayerThumbnail from './FilePlayerThumbnail';
import FilePlayerThumbnailUploading from './FilePlayerThumbnailUploading';

import FileService from '../../FileService';
import FileProcessing from '../FileProcessing';
import FileSection from '../FileSection';
import FileUploading from '../FileUploading';
import FileUploadingFailed from '../FileUploadingFailed';

import { UploadConsumer, UploadProptypes } from '@hotmart/club-files-ui';

import './FilePlayerSection.scss';

class FilePlayerSection extends PureComponent {
  static propTypes = {
    fileUpload: UploadProptypes.fileUpload,
    id: PropTypes.string,
    mediaCode: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.number,
    timestamp: PropTypes.number,
    playerMedia: PropTypes.object,
    playerKey: PropTypes.string,
    fileId: PropTypes.string,
    entityType: PropTypes.string,
    secondsLeft: PropTypes.number,
    slim: PropTypes.bool,
    progress: PropTypes.number,
    fileSignedUrl: PropTypes.string,
    status: PropTypes.string,
    playerStatus: PropTypes.string,
    maxFileSize: PropTypes.number,
    reportsLabel: PropTypes.string,
    type: PropTypes.string,
    contentType: PropTypes.string,
    reusable: PropTypes.bool,
    onSetThumbnail: PropTypes.func
  };

  static defaultProps = {
    reportsLabel: 'player_section.report'
  };

  state = {
    originalLanguage: null,
    tryMediaUploadEventSent: false
  };

  get playerKey() {
    const { playerMedia = {}, playerKey } = this.props;

    return playerMedia.playerKey || playerKey;
  }

  get status() {
    return PlayerService.getStatusCategory(this.mediaStatus);
  }

  get mediaStatus() {
    const { playerMedia = {}, playerStatus, status } = this.props;

    return playerStatus || playerMedia.status || status;
  }

  get id() {
    const { fileId, id, fileLibraryProvider, PlayerBackofficeStore } =
      this.props;

    if (fileLibraryProvider === FileLibraryProvider.PlayerBackoffice) {
      return PlayerBackofficeStore.selectedMedia?.upload_key || fileId || id;
    }

    return fileId || id;
  }

  get hasFailed() {
    return this.status === PLAYER_STATUS_CATEGORY.FAILED;
  }

  get isFinished() {
    return this.status === PLAYER_STATUS_CATEGORY.FINISHED;
  }

  get isLanguageGatewayEnabled() {
    const { hasNotified } = this.fileUpload ? this.fileUpload.info.gateway : {};

    const hasOriginalLanguage =
      this.state.originalLanguage || (hasNotified && hasNotified(this.id));
    const isEnabled =
      !hasOriginalLanguage && this.status === PLAYER_STATUS_CATEGORY.STARTED;

    return isEnabled;
  }

  get fileUpload() {
    return this.props.fileUpload;
  }

  componentDidMount() {
    this.fetchOriginalLanguage();
  }

  async fetchOriginalLanguage() {
    const shouldFetchOriginalLanguage =
      this.playerKey &&
      !(this.isLanguageGatewayEnabled || this.state.originalLanguage);

    if (shouldFetchOriginalLanguage) {
      const { language } = await PlayerService.getMediaInfo(this.playerKey);

      this.setOriginalLanguage(language);
    }
  }

  dispatchTryMediaUploadEvent({ result, statusCode }) {
    if (!this.state.tryMediaUploadEventSent && this.props.origin === 'UPLOAD') {
      DatahubEventsService.sendEvent({
        schema: new TryMediaUploadSchema({
          result,
          statusCode,
          uploadType: 'MEDIA',
          origin: this.props.moduleId ? 'MEMBERSHIP' : 'DEVICE'
        })
      });
    }

    this.setState({ tryMediaUploadEventSent: true });
  }

  render() {
    return (
      <FileSection className="file--media" slim={this.props.slim}>
        <FileSection.Header>
          <FileSection.Header.Info status={this.mediaStatus} {...this.props} />

          <FileSection.Header.Actions
            FileRemoveModal={FilePlayerRemoveModal}
            FileReplaceModal={FilePlayerReplaceModal}
            changeLabel="player_section.change_media"
            reportsLabel={this.props.reportsLabel}
            showReports={this.isFinished}
            replace={this.replace}
            remove={this.remove}
            mediaId={this.playerKey}
            download={this.download}
            downloadLabel="player_section.download_media"
          />
        </FileSection.Header>

        {this.renderBody()}
      </FileSection>
    );
  }

  renderFailed() {
    const { maxFileSize, entityType, t } = this.props;

    this.dispatchTryMediaUploadEvent({
      result: 'FAILURE',
      statusCode: HttpCodes.INTERNAL_SERVER_ERROR
    });

    return (
      <FileUploadingFailed big fileId={this.id} entityType={entityType}>
        <Html>
          {t('player_section.failed_message', {
            data: { size: formatBytes(maxFileSize) }
          })}
        </Html>
      </FileUploadingFailed>
    );
  }

  renderBody() {
    if (this.hasFailed) {
      return this.renderFailed();
    }

    return this.renderContent();
  }

  renderContent() {
    return (
      <Fragment>
        <FileSection.Content>
          {this.renderEmbed()}
          <Grid block nomargin noborder nopadding half>
            {this.renderThumbnail()}
            {this.state.originalLanguage && this.renderOriginalLanguage()}
          </Grid>
        </FileSection.Content>

        {this.isFinished && (
          <SubtitleSection
            mediaId={this.playerKey}
            originalLanguage={this.state.originalLanguage}
            setOriginalLanguage={this.setOriginalLanguage}
            videoName={this.props.name}
          />
        )}
      </Fragment>
    );
  }

  renderEmbed() {
    const { loadingThumbnail } = this.state;
    const { reusable, entityType, fileLibraryProvider } = this.props;

    if (this.status === PLAYER_STATUS_CATEGORY.FINISHED) {
      this.dispatchTryMediaUploadEvent({
        result: 'SUCCESSFUL',
        statusCode: HttpCodes.OK
      });

      return (
        <FilePlayerEmbed
          reusable={reusable}
          loading={loadingThumbnail}
          mediaId={this.playerKey}
          entityType={entityType}
          fileLibraryProvider={fileLibraryProvider}
        />
      );
    }

    return this.renderEmbedNotReady();
  }

  renderEmbedUploading = () => {
    const { progress, secondsLeft } = this.props;

    return (
      <FileUploading
        label="files.uploading_media"
        progress={progress}
        remainingTime={secondsLeft}
        isShowingRemainingTime
      />
    );
  };

  renderEmbedProcessing = () => (
    <FileProcessing label="player_section.optimizing_video" />
  );

  renderEmbedNotReady() {
    const isUploadStarted = this.status === PLAYER_STATUS_CATEGORY.STARTED;
    const renderEmbedInner = isUploadStarted
      ? this.renderEmbedUploading
      : this.renderEmbedProcessing;

    return (
      <Grid
        block
        noborder
        nomargin
        nopadding
        className="file__content__player "
      >
        <div className="file__player__embed__upload">{renderEmbedInner()}</div>
      </Grid>
    );
  }

  renderThumbnail() {
    const canEditThumbnail =
      PLAYER_STATUS_CATEGORIES.THUMBNAIL_FINISHED.includes(this.mediaStatus);

    if (canEditThumbnail) {
      return (
        <FilePlayerThumbnail
          mediaId={this.playerKey}
          mediaStatus={this.mediaStatus}
          onSetThumbnail={this.props.onSetThumbnail}
          onChangeThumbnail={({ loading }) =>
            this.setState({ loadingThumbnail: loading })
          }
        />
      );
    }

    return <FilePlayerThumbnailUploading />;
  }

  renderOriginalLanguage() {
    const { t } = this.props;

    return (
      <div className="file__content__original-language">
        <label className="mt__md">
          {t('player_section.original_language')}
        </label>
        <p>{t(`supported_languages.${this.state.originalLanguage}`)}</p>
      </div>
    );
  }

  download = async () => {
    const { entityType } = this.props;

    FileService.openFile(this.id, entityType);
  };

  remove = () => {
    if (this.fileUpload) {
      this.fileUpload.actions.delete(this.id);
      this.props.onSetThumbnail?.(null);
    }
  };

  replace = () => {
    const { reusable, fileLibraryProvider, PlayerBackofficeStore } = this.props;

    if (!this.fileUpload) {
      return null;
    }

    if (reusable) {
      PlayerBackofficeStore.replaceMediaId = this.id;

      return this.fileUpload.actions.openLibrary({
        replaceId: this.id,
        onOpenLibrary: FileService.onLibraryOpen,
        customProvider:
          fileLibraryProvider === FileLibraryProvider.PlayerBackoffice
      });
    }

    return this.fileUpload.actions.replace(this.id);
  };

  setOriginalLanguage = (originalLanguage) => {
    this.setState({ originalLanguage });
  };

  onLanguageConfirmed = async (language) => {
    await PlayerService.updateOriginalLanguage(this.playerKey, language);
    this.setOriginalLanguage(language);

    this.fileUpload && this.fileUpload.info.gateway.notify(this.id);
  };
}

export { FilePlayerSection };

export default flowRight(
  translate(),
  inject((stores) => ({
    PlayerBackofficeStore: stores.PlayerBackofficeStore,
    StreamingStore: stores.StreamingStore
  })),
  UploadConsumer()
)(FilePlayerSection);
